import { type WorkItemDto } from '@customTypes/types';
import { Box, Space, Stack, Text, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import dayjs from 'dayjs';
import { FormattedDate } from 'react-intl';

import { translate } from '@/shared/i18n/translate';
import { LBModalLayout } from '@/shared/layouts';
import { LBButton, LBDatePicker, LBListItem } from '@/shared/ui-kit/components';

export const ManageEmployerView = ({
  workItem,
  sendLastWorkDate,
  confirmArrival,
  rejectWorkItem
}: {
  workItem: WorkItemDto;
  sendLastWorkDate: (newDate: string) => void;
  confirmArrival: () => Promise<void>;
  rejectWorkItem: () => Promise<void>;
}) => {
  const form = useForm({
    initialValues: { newEndDate: dayjs().toDate() },
    transformValues: (values) => ({
      newEndDate: values.newEndDate.toISOString()
    })
  });
  const [lastDayOpened, { open: lastDayOpen, close: lastDayClose }] =
    useDisclosure();
  const [arrivalOpened, { open: arrivalOpen, close: arrivalClose }] =
    useDisclosure();
  const [workCancelOpened, { open: workCancelOpen, close: workCancelClose }] =
    useDisclosure();

  return (
    <>
      <LBModalLayout opened={lastDayOpened} onClose={lastDayClose}>
        <Title order={2}>
          {translate('popups.clubWorkInteraction.setLastDay.title')}
        </Title>
        <Space h={24} />
        <Text>
          {translate('popups.clubWorkInteraction.setLastDay.description')}
        </Text>
        <Space h={24} />
        <Stack gap={15} mb={24}>
          <Text>
            {translate('popups.clubWorkInteraction.setLastDay.setLastWorkDate')}
          </Text>
          <LBDatePicker
            key={form.key('newEndDate')}
            label={translate('commons.endWork')}
            minDate={dayjs().toDate()}
            {...form.getInputProps('newEndDate')}
            w={120}
          />
        </Stack>
        <Stack gap={5}>
          <LBButton
            fullWidth
            onClick={() => {
              sendLastWorkDate(form.getTransformedValues().newEndDate);
              lastDayClose();
            }}
          >
            {translate('commons.confirm')}
          </LBButton>
          <LBButton fullWidth variant='outline' onClick={lastDayClose}>
            {translate('commons.reject')}
          </LBButton>
        </Stack>
      </LBModalLayout>
      <LBModalLayout opened={arrivalOpened} onClose={arrivalClose}>
        <Title order={2}>
          {translate('popups.clubWorkInteraction.artistArrival.title')}
        </Title>
        <Space h={24} />
        <Text>
          {translate('popups.clubWorkInteraction.artistArrival.description')}
        </Text>
        <Space h={24} />
        <Stack gap={5}>
          <LBButton
            fullWidth
            onClick={async () => {
              await confirmArrival();
              arrivalClose();
            }}
          >
            {translate('commons.confirm')}
          </LBButton>
          <LBButton fullWidth variant='outline' onClick={arrivalClose}>
            {translate('commons.reject')}
          </LBButton>
        </Stack>
      </LBModalLayout>
      <LBModalLayout opened={workCancelOpened} onClose={workCancelClose}>
        <Title order={2}>
          {translate('popups.clubWorkInteraction.workCancel.title')}
        </Title>
        <Space h={24} />
        <Text>
          {translate('popups.clubWorkInteraction.workCancel.description')}
        </Text>
        <Space h={24} />
        <Stack gap={5}>
          <LBButton
            fullWidth
            onClick={async () => {
              await rejectWorkItem();
              workCancelClose();
            }}
          >
            {translate('commons.confirm')}
          </LBButton>
          <LBButton fullWidth variant='outline' onClick={workCancelClose}>
            {translate('commons.reject')}
          </LBButton>
        </Stack>
      </LBModalLayout>
      <Box>
        {!workItem.newEndDate &&
          workItem.arrivalConfirmed &&
          !workItem.artistRequestedEndDate && (
            <>
              <Stack gap={0}>
                <Text>
                  {translate('popups.clubWorkInteraction.artistWorkAtClub')}
                </Text>
                <Space h={10} />
                <LBListItem>
                  <Text fw={600} fz={14}>
                    {translate('commons.workAt')}
                  </Text>
                  <Text fz={14}>
                    {dayjs(workItem.startDate).format('DD.MM.YYYY').toString()}
                  </Text>
                </LBListItem>
                <LBListItem>
                  <Text fw={600} fz={14}>
                    {translate('form.approximateStartDate.label')}
                  </Text>
                  <Text fz={14}>
                    {dayjs(workItem.endDate).format('DD.MM.YYYY').toString()}
                  </Text>
                </LBListItem>
              </Stack>
              <Space h={10} />
              <LBButton fullWidth onClick={lastDayOpen}>
                {translate('commons.setLastDate')}
              </LBButton>
              <Space h={10} />
            </>
          )}

        {!workItem.arrivalConfirmed && (
          <>
            <Stack gap={15} mb={4}>
              <Text>
                {translate(
                  'popups.clubWorkInteraction.artistArrival.information'
                )}
              </Text>
            </Stack>
            <Space h={10} />
            <LBButton fullWidth onClick={arrivalOpen}>
              {translate('commons.confirm')}
            </LBButton>
            <Space h={10} />
            <LBButton fullWidth onClick={workCancelOpen}>
              {translate('commons.cancelArrangement')}
            </LBButton>
            <Space h={10} />
          </>
        )}

        {workItem.arrivalConfirmed &&
          workItem.artistRequestedEndDate &&
          !workItem.newEndDate && (
            <>
              <Stack gap={15} mb={4}>
                <Text>
                  {translate('popups.clubWorkInteraction.wantEndDate.title')}
                </Text>
              </Stack>

              <LBButton fullWidth onClick={lastDayOpen}>
                {translate('commons.setLastDate')}
              </LBButton>
              <Space h={10} />
            </>
          )}

        {workItem.newEndDate && (
          <Stack gap={10}>
            <Text>
              {translate('popups.clubWorkInteraction.newEndDate.title')}
            </Text>
            <Text>
              <FormattedDate value={workItem.newEndDate} />
            </Text>
            <Space h={10} />
          </Stack>
        )}
      </Box>
    </>
  );
};
